import { useMutation } from '@vue/apollo-composable';
import {
  GET_MY_STUDENT_PROFILE_FORM, GET_STUDENT_PROFILE_FIELD_SETTINGS,
  UPDATE_MY_STUDENT_PROFILE_FORM, UPDATE_STUDENT_PROFILE_FIELD_SETTINGS,
} from '@/graphql/studentProfileForm';
import type {
  MyStudentProfileFormQuery,
  StudentProfileFieldSettingsQuery,
  UpdateMyStudentProfileFormMutation, UpdateMyStudentProfileFormMutationVariables,
  UpdateStudentProfileFieldSettingsMutation, UpdateStudentProfileFieldSettingsMutationVariables,
} from '@/types/operations';

/**
 * Saving the student's own profile form, and the settings for which built-in
 * fields students may fill in. Both answers carry no ids for Apollo to match,
 * so each writes its result over the matching query in the cache: the form,
 * the home-page reminder and the settings card then show what was saved.
 * A student's save also refreshes their data requests, which it may answer.
 */
export function useStudentProfileFormMutations() {
  const saveMine = useMutation<UpdateMyStudentProfileFormMutation, UpdateMyStudentProfileFormMutationVariables>(
    UPDATE_MY_STUDENT_PROFILE_FORM,
    () => ({
      update: (cache, { data }) => {
        if (!data) return;
        cache.writeQuery<MyStudentProfileFormQuery>({
          query: GET_MY_STUDENT_PROFILE_FORM,
          data: { myStudentProfileForm: data.updateMyStudentProfileForm },
        });
      },
      // The answers may complete a data request: its to-do and prompt follow.
      refetchQueries: ['MyStudentDataRequests'],
    }),
  );

  const saveSettings = useMutation<UpdateStudentProfileFieldSettingsMutation, UpdateStudentProfileFieldSettingsMutationVariables>(
    UPDATE_STUDENT_PROFILE_FIELD_SETTINGS,
    () => ({
      update: (cache, { data }) => {
        if (!data) return;
        cache.writeQuery<StudentProfileFieldSettingsQuery>({
          query: GET_STUDENT_PROFILE_FIELD_SETTINGS,
          data: { studentProfileFieldSettings: data.updateStudentProfileFieldSettings },
        });
      },
    }),
  );

  return { saveMine, saveSettings };
}
